import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useGetBranches, useGetPaymentMethods } from "src/api/generic";
import { useGetAggregatorsTotalSales } from "src/api/aggreagators";
import DateRangeInput from "src/shared/components/date-range";
import Lottie from "react-lottie";
import * as animationData from "src/assets/animated/loader.json";
import SummaryCards from "./summary-cards";
import LiveControls from "./live-controls";

const lottieOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

interface IProps {
  dateRange: {
    startDate: string;
    endDate: string;
  };
  setDateRange: (range: { startDate: string; endDate: string }) => void;
}

const WhatIfAnalysis = ({ dateRange, setDateRange }: IProps) => {
  const { t } = useTranslation("aggregators");
  const { t: generalT } = useTranslation("general");

  const [priceAdjustment, setPriceAdjustment] = useState(0);
  const [costAdjustment, setCostAdjustment] = useState(0);
  const [modifiersAdjustment, setModifiersAdjustment] = useState(0);
  const [commissionAdjustment, setCommissionAdjustment] = useState(0);

  const [payments, setPayments] = useState<number[]>([]);
  const [tempSelectedPayments, setTempSelectedPayments] = useState<number[]>(
    []
  );
  const [branches, setBranches] = useState<number[]>([]);
  const [tempSelectedBranches, setTempSelectedBranches] = useState<number[]>(
    []
  );
  const [endDateFocused, setEndDateFocused] = useState(false);

  const { data: paymentsData } = useGetPaymentMethods();
  const { data: branchesData } = useGetBranches();

  const {
    data: salesData,
    refetch,
    isFetching,
  } = useGetAggregatorsTotalSales({
    start_date: dateRange.startDate,
    end_date: dateRange.endDate,
    ...(payments.length > 0 && { payment_methods: payments }),
    ...(branches.length > 0 && { branches: branches }),
  });

  useEffect(() => {
    const startDate = dateRange.startDate ? moment(dateRange.startDate) : null;
    const endDate = dateRange.endDate ? moment(dateRange.endDate) : null;
    if (startDate && endDate && endDate.diff(startDate, "days") < 31) {
      refetch();
    }
  }, [payments, dateRange.endDate, dateRange.startDate, branches]);

  const original = salesData?.data;

  const computed = useMemo(() => {
    if (!original) return null;

    const origNetSales = Number(original.net_sales);
    const origCogs = Number(original.cogs);
    const origAggFees = Number(original.aggregator_fee);
    const origProfit = origNetSales - origCogs - origAggFees;

    // Price adjustment: affects Net Sales, COGS, and Agg Fees
    const currentNetSales =
      origNetSales + origNetSales * (priceAdjustment / 100);

    // COGS affected by: price adj + product cost adj + modifiers cost adj
    const currentCogs =
      origCogs +
      origCogs * (priceAdjustment / 100) +
      origCogs * (costAdjustment / 100) +
      origCogs * (modifiersAdjustment / 100);

    // Agg fees affected by: price adj + commission adj
    const currentAggFees =
      origAggFees +
      origAggFees * (priceAdjustment / 100) +
      origAggFees * (commissionAdjustment / 100);

    const currentProfit = currentNetSales - currentCogs - currentAggFees;
    const cogsPercent =
      currentNetSales > 0 ? (currentCogs / currentNetSales) * 100 : 0;
    const aggFeePercent =
      currentNetSales > 0 ? (currentAggFees / currentNetSales) * 100 : 0;
    const profitMargin =
      currentNetSales > 0 ? (currentProfit / currentNetSales) * 100 : 0;

    // Profit Change = current profit - original profit
    const profitChange = currentProfit - origProfit;

    return {
      netSales: Math.round(currentNetSales * 100) / 100,
      totalCogs: Math.round(currentCogs * 100) / 100,
      cogsPercent: Math.round(cogsPercent * 10) / 10,
      aggregatorFees: Math.round(currentAggFees * 100) / 100,
      aggFeePercent: Math.round(aggFeePercent * 10) / 10,
      netProfit: Math.round(currentProfit * 100) / 100,
      profitMargin: Math.round(profitMargin * 10) / 10,
      profitChange: Math.round(profitChange * 100) / 100,
    };
  }, [
    original,
    priceAdjustment,
    costAdjustment,
    modifiersAdjustment,
    commissionAdjustment,
  ]);

  const handleReset = () => {
    setPriceAdjustment(0);
    setCostAdjustment(0);
    setModifiersAdjustment(0);
    setCommissionAdjustment(0);
  };

  const handleClosePayments = () => {
    setPayments(tempSelectedPayments);
  };

  const handleChangePayments = (event: any) => {
    const {
      target: { value },
    } = event;
    setTempSelectedPayments(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleCloseBranches = () => {
    setBranches(tempSelectedBranches);
  };

  const handleChangeBranches = (event: any) => {
    const {
      target: { value },
    } = event;
    setTempSelectedBranches(
      typeof value === "string" ? value.split(",") : value
    );
  };

  const isOutsideRange = (day: moment.Moment) => {
    const startDate = dateRange.startDate ? moment(dateRange.startDate) : null;
    const endDate = day ? moment(day) : null;
    const range =
      startDate && endDate ? endDate.diff(startDate, "days") : null;
    return endDateFocused && range !== null && (range < 0 || range > 30);
  };

  return (
    <Stack gap={3} pb={4}>
      <Stack
        sx={{ flexDirection: { xs: "column", md: "row" } }}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box mb={{ xs: "24px", md: 0 }}>
          <Typography
            fontSize={"20px"}
            fontWeight={600}
            lineHeight={"28px"}
            color={"#101828"}
          >
            {t("whatIfTitle")}
          </Typography>
          <Typography
            fontSize={"14px"}
            color={"#667085"}
            lineHeight={"20px"}
            mt={0.5}
          >
            {t("whatIfDescription")}
          </Typography>
        </Box>
        <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
          <Box sx={{ minWidth: 207, maxWidth: { xs: "90vw", sm: 207 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>{generalT("branches")}</InputLabel>
              <Select
                multiple
                value={tempSelectedBranches}
                onChange={handleChangeBranches}
                onClose={handleCloseBranches}
                input={<OutlinedInput label={generalT("branches")} />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? t("allBranches")
                    : selected
                        .map(
                          (id: number) =>
                            branchesData?.data.find(
                              (b: any) => b.id === +id
                            )?.name
                        )
                        .join(", ")
                }
              >
                {branchesData?.data.map((branch) => (
                  <MenuItem
                    key={branch.id}
                    value={branch.id}
                    sx={{ padding: "0" }}
                  >
                    <Checkbox
                      size="small"
                      checked={tempSelectedBranches.indexOf(branch.id) > -1}
                    />
                    <ListItemText primary={branch.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 207, maxWidth: { xs: "90vw", sm: 207 } }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("payments")}</InputLabel>
              <Select
                multiple
                value={tempSelectedPayments}
                onChange={handleChangePayments}
                onClose={handleClosePayments}
                input={<OutlinedInput label={t("payments")} />}
                renderValue={(selected) =>
                  selected
                    .map(
                      (id: number) =>
                        paymentsData?.data.find(
                          (p: any) => p.id === +id
                        )?.name
                    )
                    .join(", ")
                }
              >
                {paymentsData?.data.map((payment) => (
                  <MenuItem
                    key={payment.id}
                    value={payment.id}
                    sx={{ padding: "0" }}
                  >
                    <Checkbox
                      size="small"
                      checked={tempSelectedPayments.indexOf(payment.id) > -1}
                    />
                    <ListItemText primary={payment.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 207 }}>
            <DateRangeInput
              startDate={
                dateRange.startDate ? moment(dateRange.startDate) : null
              }
              startDateId="whatif_start_date"
              endDate={dateRange.endDate ? moment(dateRange.endDate) : null}
              endDateId="whatif_end_date"
              onDatesChange={(arg: {
                startDate: moment.Moment | null;
                endDate: moment.Moment | null;
              }) => {
                setDateRange({
                  startDate: arg.startDate?.format("yyyy-MM-DD") as string,
                  endDate: arg.endDate?.format("yyyy-MM-DD") as string,
                });
              }}
              isOutsideRange={isOutsideRange}
              onFocusChange={(focused: "startDate" | "endDate" | null) => {
                setEndDateFocused(focused === "endDate");
              }}
            />
          </Box>
        </Stack>
      </Stack>

      {isFetching ? (
        <Box display={"flex"} alignItems={"center"} height={"60vh"}>
          <Lottie options={lottieOptions} height={400} width={400} />
        </Box>
      ) : computed ? (
        <>
          <SummaryCards
            netSales={computed.netSales}
            totalCogs={computed.totalCogs}
            cogsPercent={computed.cogsPercent}
            aggregatorFees={computed.aggregatorFees}
            aggregatorFeePercent={computed.aggFeePercent}
            netProfit={computed.netProfit}
            profitMargin={computed.profitMargin}
            profitChange={computed.profitChange}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <LiveControls
                priceAdjustment={priceAdjustment}
                costAdjustment={costAdjustment}
                modifiersAdjustment={modifiersAdjustment}
                commissionAdjustment={commissionAdjustment}
                onPriceChange={setPriceAdjustment}
                onCostChange={setCostAdjustment}
                onModifiersChange={setModifiersAdjustment}
                onCommissionChange={setCommissionAdjustment}
                onReset={handleReset}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                p={"20px"}
                border={"1px solid #EAECF0"}
                borderRadius={"12px"}
                bgcolor={"#FFFFFF"}
                sx={{
                  background:
                    "linear-gradient(180deg, #FFFFFF 0%, #FFFBF5 60%, #FFF5E9 100%)",
                }}
                height={"100%"}
              >
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={1.5}
                  mb={1.5}
                >
                  <Box
                    width={"36px"}
                    height={"36px"}
                    borderRadius={"50%"}
                    bgcolor={"#FF8D3F"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Typography fontSize={"18px"} color={"#FFFFFF"}>
                      💡
                    </Typography>
                  </Box>
                  <Typography
                    fontSize={"16px"}
                    fontWeight={600}
                    color={"#101828"}
                  >
                    {t("aiInsight")}
                  </Typography>
                </Stack>
                <Typography
                  fontSize={"14px"}
                  color={"#98A2B3"}
                  fontStyle={"italic"}
                >
                  {t("aiInsightComingSoon")}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box>
            <Typography
              fontSize={"16px"}
              fontWeight={600}
              color={"#101828"}
              mb={2}
            >
              {t("productPerformance")}
            </Typography>
            <Box
              p={"40px"}
              border={"1px solid #EAECF0"}
              borderRadius={"12px"}
              bgcolor={"#F9FAFB"}
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              minHeight={"200px"}
            >
              <Typography
                fontSize={"16px"}
                color={"#98A2B3"}
                fontStyle={"italic"}
              >
                {t("aiInsightComingSoon")}
              </Typography>
            </Box>
          </Box>
        </>
      ) : null}
    </Stack>
  );
};

export default WhatIfAnalysis;
